import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, type Params, Router } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  take,
} from 'rxjs/operators';
import type { ResBody as CheckPasswordResBody } from '../../../server/routes/auth/check-password';
import * as AuthRegister from '../../../server/routes/auth/register';
import { passwordConfig } from '../../../server/services/password-config.service';
import { nameMaxLength } from '../../../server/services/utils.service';
import { AuthUserService } from '../auth-user.service';
import { HomepageLinksService } from '../homepage-links.service';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-register-email',
  templateUrl: './register-email.component.html',
  styleUrls: ['./register-email.component.scss'],
  standalone: false,
})
export class RegisterEmailComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private titleService = inject(TitleService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  public homepageLinks = inject(HomepageLinksService);
  private utils = inject(UtilsService);

  form = new FormGroup({
    name: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(nameMaxLength)],
      nonNullable: true,
    }),
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
    password: new FormControl('', {
      validators: [
        Validators.required,
        Validators.maxLength(passwordConfig.maxLength),
      ],
      nonNullable: true,
    }),
  });

  submitSuccess = false;
  waiting = false;
  formErrorMessage?: string;
  disableSubmit = true;

  errorState?: 'unknownError' | 'noConnection';

  // #DuplicatePasswordCode - other components have copies of the below code
  // Password related interface features
  passwordSubject: Subject<string>;
  currentFormPassword: string;
  passwordErrorMessage?: string;
  passwordGuessesLog10 = 0;
  passwordProgressBarValue = 0;

  loginRegisterSwitchQueryParams: Params;
  redirectUrl: string;

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Register');

    // The following is to ensure that when a user is redirected for the
    // purposes of logging in that they are still redirected to the correct
    // on register success.
    const routeSubscribe = this.route.queryParams.subscribe((params) => {
      this.loginRegisterSwitchQueryParams = params;

      this.redirectUrl = params.redirect ?? '/';

      // If the user is already logged in (say user clicks back button or
      // reloads) redirect immediately
      const authUserSubscribe = this.authUserService.userOrgObj
        .pipe(take(1))
        .subscribe((user) => {
          if (user) this.router.navigateByUrl(this.redirectUrl);
        });
      this.subscriptions.add(authUserSubscribe);
    });
    this.subscriptions.add(routeSubscribe);

    // #DuplicatePasswordCode - other components have copies of the below code
    this.passwordSubject = new Subject<string>();
    const passwordSubscribe = this.passwordSubject
      .pipe(
        debounceTime(1000),
        filter((value) => value.length > 0),
        distinctUntilChanged()
      )
      .subscribe((password) => {
        this.checkPassword(password);
      });
    this.subscriptions.add(passwordSubscribe);

    const formSubscribe = this.form.valueChanges.subscribe((formData) => {
      if (typeof formData.password === 'string')
        this.updatePassword(formData.password);

      this.disableSubmit = this.form.invalid;
    });
    this.subscriptions.add(formSubscribe);
  }

  // #DuplicatePasswordCode - other components have copies of the below code
  updatePassword(password: string): void {
    // If nothing has changed, do nothing
    if (password === this.currentFormPassword) return;
    // Clear errors
    this.form.controls.password.setErrors(null);

    // this.currentFormPassword is used to check that subsequent updates don't show this earlier result
    this.currentFormPassword = password;
    if (password.length === 0) {
      this.form.controls.password.setErrors({ incorrect: true });
      this.passwordErrorMessage = 'Required.';
      return;
    }
    this.passwordSubject.next(password);
  }

  // #DuplicatePasswordCode - other components have copies of the below code
  checkPassword(password: string): void {
    // Only check if the password is still the currentFormPassword
    if (password !== this.currentFormPassword) return;

    const httpSubscribe = this.http
      .post<CheckPasswordResBody>('/api/auth/check-password', {
        password,
      })
      .subscribe({
        next: (response) => {
          // Ensure that the password has not been updated recently
          if (password !== this.currentFormPassword) return;
          this.passwordProgressBarValue =
            response.guessesLog10 > 12
              ? 100
              : (100 / 12) * response.guessesLog10;
          this.passwordGuessesLog10 = response.guessesLog10;
          if (
            password.length < passwordConfig.minLength ||
            response.guessesLog10 < passwordConfig.minGuessesLog10
          ) {
            this.form.controls.password.setErrors({ incorrect: true });
            this.passwordErrorMessage =
              'Password must be at least ' +
              passwordConfig.minLength +
              ' characters and hard to guess.';
            this.form.controls.password.markAllAsTouched();
          } else {
            this.form.controls.password.setErrors(null);
          }
        },
        error: (errorResponse) => {
          this.formErrorMessage = 'There may be a connection issue.';
          console.error(errorResponse);
        },
      });
    this.subscriptions.add(httpSubscribe);
  }

  // #DuplicatePasswordCode - other components have copies of the below code
  updatePasswordProgressColor(passwordGuessesLog10: number): string {
    // we take in passwordGuessesLog10 because it is necessary for Angular to
    // observe it update in order for the class to be updated on change
    if (
      passwordGuessesLog10 < passwordConfig.minGuessesLog10 ||
      this.currentFormPassword.length < passwordConfig.minLength
    ) {
      return 'warn';
    } else if (passwordGuessesLog10 < passwordConfig.goodGuessesLog10) {
      return 'accent';
    } else {
      return 'accent';
    }
  }

  submit(formData): void {
    if (this.form.invalid) return;

    // Clear state from previous submissions
    this.formErrorMessage = undefined;
    this.form.controls.password.setErrors(null);
    this.passwordErrorMessage = undefined;

    const { name, email, password } = formData;

    this.waiting = true;
    const sub = this.recaptchaV3Service.execute('register').subscribe({
      next: (recaptcha) => {
        const body: AuthRegister.ReqBody = {
          name,
          email,
          password,
          recaptcha,
        };
        const httpSubscribe = this.http
          .post<AuthRegister.ResBody>('/api/auth/register', body)
          .subscribe({
            next: ({ confirmEmailCodeLink }) => {
              this.waiting = false;
              this.errorState = undefined;
              this.router.navigate(['/confirm-email'], {
                queryParams: { confirmEmailCodeLink, email, sentMode: 1 },
              });
            },
            error: (error) => {
              this.waiting = false;
              if (error.status === 0) {
                this.utils.launchOfflineNotification();
                this.errorState = 'noConnection';
              } else {
                this.errorState = 'unknownError';
              }

              this.formErrorMessage = undefined;
            },
          });
        this.subscriptions.add(httpSubscribe);
      },
      error: (error) => {
        console.error(`Recaptcha v3 error:`, error);
        throw error;
      },
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
